/**
 * Sprint P32 · Project Explorer sidebar for the Cockpit.
 * Layer toggles + outlier control + mine-design overlay.
 */
import { Cube, MapPin, Compass, Ruler, Warning } from "@phosphor-icons/react";


export function ProjectExplorer({
  counts, layers, setLayers,
  showMineDesignPit, setShowMineDesignPit,
  pitPresent, onFitOverlay,
  outlierHoles, includeOutliers, setIncludeOutliers,
}) {
  const items = [
    { key: "blocks",   label: "Block Model",     icon: Cube,   count: counts.blocks },
    { key: "ore",      label: "  Ore (≥ cutoff)", icon: Cube,   count: counts.ore, indent: true },
    { key: "waste",    label: "  Waste",          icon: Cube,   count: counts.waste, indent: true },
    { key: "traces",   label: "Drillhole traces", icon: Ruler,  count: counts.holes },
    { key: "labels",   label: "  Collar labels",  icon: MapPin, count: counts.holes, indent: true },
    { key: "shell",    label: "Grade shell (iso)", icon: Cube, count: null, gold: true },
    { key: "wireframe", label: "Implicit wireframe", icon: Cube, count: null, teal: true },
    { key: "grid",     label: "XYZ grid",         icon: Compass, count: null },
  ];
  return (
    <div className="w-full flex-1 flex flex-col text-slate-200"
         data-testid="cockpit-explorer">
      <div className="px-3 py-2 border-b border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-sky-400">
          Project Explorer
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {items.map((it) => (
          <label key={it.key}
                 className={`flex items-center gap-2 px-3 py-1.5 hover:bg-slate-800 cursor-pointer text-[12px] ${it.gold ? "text-amber-300" : it.teal ? "text-cyan-300" : "text-slate-200"}`}
                 data-testid={`cockpit-layer-${it.key}`}>
            <input type="checkbox"
                   checked={!!layers[it.key]}
                   onChange={(e) => setLayers({ ...layers, [it.key]: e.target.checked })}
                   className={`rounded ${it.gold ? "accent-amber-500" : it.teal ? "accent-cyan-500" : "accent-sky-500"}`} />
            <it.icon size={12} className={`${it.indent ? "opacity-60" : ""} ${it.gold ? "text-amber-400" : ""} ${it.teal ? "text-cyan-400" : ""}`} />
            <div className="flex-1 truncate">{it.label}</div>
            {it.count !== null && (
              <div className="text-[9px] font-mono text-slate-400">{it.count}</div>
            )}
          </label>
        ))}

        {/* Outlier drillholes control */}
        {outlierHoles > 0 && (
          <label className="mx-3 my-2 flex items-center gap-2 text-[10px] text-amber-300 cursor-pointer"
                 data-testid="cockpit-include-outliers">
            <input type="checkbox" checked={includeOutliers}
                   onChange={(e) => setIncludeOutliers(e.target.checked)}
                   className="rounded accent-amber-500" />
            <Warning size={12} weight="duotone" />
            Include {outlierHoles} regional outlier hole{outlierHoles > 1 ? "s" : ""}
          </label>
        )}

        {/* Mine design overlay layer */}
        <div className="px-3 pt-3 pb-1 text-[9px] font-mono uppercase tracking-widest text-sky-400 border-t border-slate-800">
          Mine Design
        </div>
        <label className={`flex items-center gap-2 px-3 py-1.5 text-[12px] ${
              pitPresent ? "hover:bg-slate-800 cursor-pointer text-slate-200" : "text-slate-500 cursor-not-allowed"
            }`}
              data-testid="cockpit-layer-mine-pit">
          <input type="checkbox" disabled={!pitPresent}
                 checked={showMineDesignPit && pitPresent}
                 onChange={(e) => setShowMineDesignPit(e.target.checked)}
                 className="rounded accent-sky-500" />
          <Cube size={12} className="opacity-60" />
          <div className="flex-1 truncate">Engineering open-pit</div>
          {pitPresent
            ? <span className="text-[9px] font-mono text-emerald-400">present</span>
            : <span className="text-[9px] font-mono text-slate-500">—</span>}
        </label>
        {showMineDesignPit && (
          <button type="button" onClick={onFitOverlay}
                  className="mx-3 my-2 px-2 py-1 text-[10px] uppercase tracking-widest font-mono rounded border border-slate-700 text-slate-300 hover:bg-slate-800 w-[calc(100%-1.5rem)]"
                  data-testid="cockpit-fit-mine-design">
            Fit camera to mine design
          </button>
        )}
      </div>
    </div>
  );
}
